// curl ↔ rest request converter.
//
// Goals:
//   - Parse the curl commands that people actually paste (Stripe docs,
//     browser DevTools "Copy as cURL", Postman exports, hand-written).
//   - Emit shell-safe curl strings that round-trip the same RequestSpec.
//
// What's intentionally not supported (yet): @file body inputs, certificate
// flags, --resolve, --proxy, and other niche network-config options. Those
// pass through the lexer fine but the result just records them on
// `unsupported` so the UI can warn.

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";
export type BodyType = "none" | "json" | "text" | "form" | "multipart";

export interface KV { key: string; value: string; enabled: boolean }

export interface RequestSpec {
  name: string;
  method: Method;
  url: string;
  headers: KV[];
  query: KV[];
  bodyType: BodyType;
  body: string | null;
  auth: { kind: "none" | "basic" | "bearer"; fields: Record<string, string> };
  // Hints retained for round-trip parity.
  followRedirects: boolean;
  insecure: boolean;
  unsupported: string[];
}

// ── Shell tokenizer ─────────────────────────────────────────────────────
//
// Splits a curl command line into argv tokens. Handles:
//   • single quotes (literal)
//   • double quotes (literal, but backslash-escaped characters inside)
//   • backslash + newline → line continuation
//   • $'…' ANSI-C escapes (\n \r \t \\ \' \" \xNN \uNNNN)
//   • whitespace separation
// Doesn't handle: variable expansion (we don't want it for curl-import).

export function tokenize(src: string): string[] {
  const out: string[] = [];
  let cur = "";
  let i = 0;
  let inToken = false;
  const peek = (n = 0) => src[i + n];

  while (i < src.length) {
    const ch = src[i];

    // Line continuation: backslash + (\r? \n).
    if (ch === "\\" && (peek(1) === "\n" || (peek(1) === "\r" && peek(2) === "\n"))) {
      i += peek(1) === "\r" ? 3 : 2;
      continue;
    }
    if (ch === "\\" && peek(1) !== undefined && !/\s/.test(peek(1)!)) {
      // Generic escape outside quotes: take next char verbatim.
      cur += peek(1);
      i += 2;
      inToken = true;
      continue;
    }
    if (/\s/.test(ch)) {
      if (inToken) { out.push(cur); cur = ""; inToken = false; }
      i++;
      continue;
    }
    if (ch === "'") {
      i++;
      inToken = true;
      while (i < src.length && src[i] !== "'") { cur += src[i]; i++; }
      i++; // skip closing '
      continue;
    }
    if (ch === '"') {
      i++;
      inToken = true;
      while (i < src.length && src[i] !== '"') {
        if (src[i] === "\\" && i + 1 < src.length) {
          // Inside double-quotes, only a few characters get escaped.
          const nxt = src[i + 1];
          if (nxt === '"' || nxt === "\\" || nxt === "$" || nxt === "`" || nxt === "\n") {
            cur += nxt;
            i += 2;
          } else {
            cur += src[i]; i++;
          }
        } else {
          cur += src[i]; i++;
        }
      }
      i++; // skip closing "
      continue;
    }
    if (ch === "$" && peek(1) === "'") {
      // ANSI-C quoting: $'…'
      i += 2;
      inToken = true;
      while (i < src.length && src[i] !== "'") {
        if (src[i] === "\\" && i + 1 < src.length) {
          const c = src[i + 1];
          const map: Record<string, string> = { n: "\n", r: "\r", t: "\t", b: "\b", f: "\f", a: "\x07", v: "\x0b", "\\": "\\", "'": "'", '"': '"', "0": "\0", e: "\x1b", "?": "?" };
          if (map[c] !== undefined) { cur += map[c]; i += 2; continue; }
          if (c === "x" && i + 3 < src.length) { cur += String.fromCharCode(parseInt(src.slice(i + 2, i + 4), 16)); i += 4; continue; }
          if (c === "u" && i + 5 < src.length) { cur += String.fromCharCode(parseInt(src.slice(i + 2, i + 6), 16)); i += 6; continue; }
          cur += c;
          i += 2;
        } else {
          cur += src[i]; i++;
        }
      }
      i++;
      continue;
    }
    cur += ch;
    i++;
    inToken = true;
  }
  if (inToken) out.push(cur);
  return out;
}

// ── Parse ───────────────────────────────────────────────────────────────

// Maps short flags to long-form. Only flags we care about for HTTP semantics.
const SHORT_ALIASES: Record<string, string> = {
  "-X": "--request",
  "-H": "--header",
  "-d": "--data",
  "-F": "--form",
  "-u": "--user",
  "-A": "--user-agent",
  "-b": "--cookie",
  "-e": "--referer",
  "-L": "--location",
  "-k": "--insecure",
  "-I": "--head",
  "-G": "--get",
  "-o": "--output",
  "-O": "--remote-name",
  "-s": "--silent",
  "-S": "--show-error",
  "-v": "--verbose",
  "-f": "--fail",
  "-i": "--include",
  "-x": "--proxy",
  "-#": "--progress-bar",
};

// Long-form flags that take a value (consume the next token).
const VALUE_FLAGS = new Set<string>([
  "--request", "--header", "--data", "--data-raw", "--data-ascii", "--data-binary",
  "--data-urlencode", "--form", "--form-string", "--user", "--user-agent",
  "--cookie", "--referer", "--proxy", "--proxy-user", "--cacert", "--cert",
  "--key", "--connect-timeout", "--max-time", "--retry", "--output", "--url",
  "--range", "--compressed-ssh", "--http1.1", "--http2",
]);
// Boolean (value-less) flags we know about.
const BOOLEAN_FLAGS = new Set<string>([
  "--location", "--insecure", "--head", "--get", "--silent", "--show-error",
  "--verbose", "--fail", "--include", "--progress-bar", "--remote-name",
  "--compressed", "--http1.0",
]);

interface ParseOptions { name?: string }

export function parse(input: string, opts: ParseOptions = {}): RequestSpec {
  const argv = tokenize(input).filter((t) => t.length > 0);
  // If the first token is "curl", drop it. Otherwise be lenient — some folks
  // paste only the flags.
  if (argv[0] === "curl") argv.shift();

  const out: RequestSpec = {
    name: opts.name ?? "Imported request",
    method: "GET",
    url: "",
    headers: [],
    query: [],
    bodyType: "none",
    body: null,
    auth: { kind: "none", fields: {} },
    followRedirects: false,
    insecure: false,
    unsupported: [],
  };

  const dataParts: Array<{ kind: "raw" | "urlencode"; key?: string; value: string }> = [];
  const formParts: Array<{ key: string; value: string }> = [];
  let useGet = false;
  let explicitMethod: Method | null = null;

  for (let i = 0; i < argv.length; i++) {
    let tok = argv[i];
    if (tok === "--") continue;

    // -XPOST or -d'…' compound short option: split.
    if (tok.startsWith("-") && !tok.startsWith("--") && tok.length > 2 && SHORT_ALIASES[tok.slice(0, 2)]) {
      const inline = tok.slice(2);
      tok = tok.slice(0, 2);
      argv.splice(i + 1, 0, inline);
    }

    // --flag=value form.
    let inlineValue: string | null = null;
    if (tok.startsWith("--") && tok.includes("=")) {
      const eq = tok.indexOf("=");
      inlineValue = tok.slice(eq + 1);
      tok = tok.slice(0, eq);
    }

    if (SHORT_ALIASES[tok]) tok = SHORT_ALIASES[tok];

    if (tok === "--request") {
      const v = inlineValue ?? argv[++i] ?? "";
      explicitMethod = normalizeMethod(v);
    } else if (tok === "--head") {
      explicitMethod = "HEAD";
    } else if (tok === "--get") {
      useGet = true;
    } else if (tok === "--header") {
      const v = inlineValue ?? argv[++i] ?? "";
      const colon = v.indexOf(":");
      if (colon >= 0) {
        const k = v.slice(0, colon).trim();
        const val = v.slice(colon + 1).replace(/^\s+/, "").replace(/^["']|["']$/g, "");
        if (k) out.headers.push({ key: k, value: val, enabled: true });
      }
    } else if (tok === "--data" || tok === "--data-raw" || tok === "--data-ascii" || tok === "--data-binary") {
      const v = inlineValue ?? argv[++i] ?? "";
      dataParts.push({ kind: "raw", value: v });
    } else if (tok === "--data-urlencode") {
      const v = inlineValue ?? argv[++i] ?? "";
      const eq = v.indexOf("=");
      if (eq >= 0) dataParts.push({ kind: "urlencode", key: v.slice(0, eq), value: v.slice(eq + 1) });
      else dataParts.push({ kind: "urlencode", value: v });
    } else if (tok === "--form" || tok === "--form-string") {
      const v = inlineValue ?? argv[++i] ?? "";
      const eq = v.indexOf("=");
      if (eq >= 0) formParts.push({ key: v.slice(0, eq), value: v.slice(eq + 1) });
    } else if (tok === "--user") {
      const v = inlineValue ?? argv[++i] ?? "";
      const colon = v.indexOf(":");
      const user = colon >= 0 ? v.slice(0, colon) : v;
      const pass = colon >= 0 ? v.slice(colon + 1) : "";
      out.auth = { kind: "basic", fields: { username: user, password: pass } };
    } else if (tok === "--user-agent") {
      const v = inlineValue ?? argv[++i] ?? "";
      out.headers.push({ key: "User-Agent", value: v, enabled: true });
    } else if (tok === "--cookie") {
      const v = inlineValue ?? argv[++i] ?? "";
      out.headers.push({ key: "Cookie", value: v, enabled: true });
    } else if (tok === "--referer") {
      const v = inlineValue ?? argv[++i] ?? "";
      out.headers.push({ key: "Referer", value: v, enabled: true });
    } else if (tok === "--location") {
      out.followRedirects = true;
    } else if (tok === "--insecure") {
      out.insecure = true;
    } else if (tok === "--compressed") {
      // curl sends Accept-Encoding: deflate, gzip — we just record the flag.
      if (!out.headers.some((h) => h.key.toLowerCase() === "accept-encoding")) {
        out.headers.push({ key: "Accept-Encoding", value: "gzip, deflate, br", enabled: true });
      }
    } else if (tok === "--url") {
      const v = inlineValue ?? argv[++i] ?? "";
      out.url = v;
    } else if (BOOLEAN_FLAGS.has(tok)) {
      // Known no-op for our purposes — ignore but don't warn.
    } else if (VALUE_FLAGS.has(tok)) {
      // Consume the value but record as unsupported so the UI can warn.
      const v = inlineValue ?? argv[++i] ?? "";
      out.unsupported.push(`${tok} ${v}`);
    } else if (tok.startsWith("-")) {
      out.unsupported.push(tok);
    } else {
      // Positional → URL.
      if (!out.url) out.url = tok;
      else out.unsupported.push(`extra:${tok}`);
    }
  }

  // ── Resolve body / query / method ────────────────────────────────────

  if (formParts.length) {
    out.bodyType = "multipart";
    out.body = formParts.map((p) => `${p.key}=${p.value}`).join("\n");
    if (!out.headers.some((h) => h.key.toLowerCase() === "content-type")) {
      // Multipart Content-Type is set by the client; we record it for clarity.
      out.headers.push({ key: "Content-Type", value: "multipart/form-data", enabled: true });
    }
  }

  // --data semantics:
  //   • If the user passed --data and used -G/--get, the data becomes query
  //     parameters instead of a body.
  //   • Otherwise, concatenate raw data parts with & like curl does, and
  //     decode each segment back into KV pairs ONLY if everything looks
  //     URL-encoded form data and no explicit content-type was set.
  const rawData = dataParts.filter((p) => p.kind === "raw").map((p) => p.value);
  const urlencodeData = dataParts.filter((p) => p.kind === "urlencode");
  const joinedRaw = rawData.join("&");

  if (useGet) {
    const params = new URLSearchParams();
    if (joinedRaw) for (const [k, v] of new URLSearchParams(joinedRaw)) params.append(k, v);
    for (const u of urlencodeData) params.append(u.key ?? "", u.value);
    if (out.url) {
      try {
        const u = new URL(out.url);
        for (const [k, v] of params) u.searchParams.append(k, v);
        out.url = u.toString();
      } catch {
        out.url += (out.url.includes("?") ? "&" : "?") + params.toString();
      }
    }
  } else if (dataParts.length) {
    // Detect content type.
    const ctHeader = out.headers.find((h) => h.key.toLowerCase() === "content-type");
    const ct = (ctHeader?.value ?? "").toLowerCase();
    if (ct.includes("application/json") || ((!ctHeader) && looksLikeJson(joinedRaw))) {
      out.bodyType = "json";
      out.body = joinedRaw;
      if (!ctHeader) out.headers.push({ key: "Content-Type", value: "application/json", enabled: true });
    } else if (ct.includes("application/x-www-form-urlencoded") || (!ctHeader && looksLikeForm(joinedRaw))) {
      out.bodyType = "form";
      // Append urlencode parts too (they're already key=value-ish).
      const combined = [joinedRaw, ...urlencodeData.map((u) => `${u.key ?? ""}=${encodeURIComponent(u.value)}`).filter(Boolean)].filter(Boolean).join("&");
      out.body = combined;
      if (!ctHeader) out.headers.push({ key: "Content-Type", value: "application/x-www-form-urlencoded", enabled: true });
    } else {
      out.bodyType = "text";
      out.body = joinedRaw;
    }
  }

  // Method default rules:
  //   • Explicit -X wins.
  //   • If there's a body and no -X, curl uses POST.
  //   • -G/--get forces GET (already set).
  //   • -I/--head forces HEAD.
  if (explicitMethod) out.method = explicitMethod;
  else if (useGet) out.method = "GET";
  else if (out.bodyType !== "none") out.method = "POST";
  else out.method = "GET";

  // Split URL query string into out.query so the UI shows them as rows
  // instead of mashed into the URL.
  if (out.url) {
    try {
      const u = new URL(out.url);
      u.searchParams.forEach((v, k) => out.query.push({ key: k, value: v, enabled: true }));
      const rebuilt = u.origin + u.pathname;
      out.url = rebuilt;
    } catch { /* leave as-is if it's not absolute */ }
  }

  // If basic auth came from -u, also surface the encoded header so the
  // request sent to the worker (which doesn't know about the auth config
  // table at run time) carries it. The auth.kind = "basic" stays so any
  // future "regenerate header" logic works.
  if (out.auth.kind === "basic" && !out.headers.some((h) => h.key.toLowerCase() === "authorization")) {
    const enc = b64(`${out.auth.fields.username ?? ""}:${out.auth.fields.password ?? ""}`);
    out.headers.push({ key: "Authorization", value: `Basic ${enc}`, enabled: true });
  }

  return out;
}

function normalizeMethod(s: string): Method {
  const u = s.toUpperCase();
  return (["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"] as Method[]).includes(u as Method) ? (u as Method) : "GET";
}

function looksLikeJson(s: string): boolean {
  const t = s.trim();
  if (!t) return false;
  if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
    try { JSON.parse(t); return true; } catch { return false; }
  }
  return false;
}
function looksLikeForm(s: string): boolean {
  if (!s) return false;
  // Mostly k=v&k=v pairs with no obvious JSON.
  return /^[^=&{[]+=[^&]*(&[^=&]+=[^&]*)*$/.test(s);
}

// Browser/Node-portable base64 of a UTF-8 string.
function b64(s: string): string {
  if (typeof btoa === "function") {
    return btoa(unescape(encodeURIComponent(s)));
  }
  // Node fallback.
  return Buffer.from(s, "utf8").toString("base64");
}

// ── Emit ────────────────────────────────────────────────────────────────

// Shell-quote a string for safe inclusion in a curl command line. Uses
// single quotes (so backslash etc don't get interpreted) and escapes any
// inner ' with '\''.
function q(s: string): string {
  if (s === "") return "''";
  if (/^[A-Za-z0-9_@%+=:,./-]+$/.test(s)) return s; // safe bare token
  return "'" + s.replace(/'/g, "'\\''") + "'";
}

export interface EmitOptions {
  /** Insert "\\\n  " between flags for readability. Default true. */
  multiline?: boolean;
  /** Include --location (default true) so the emitted curl follows redirects. */
  followRedirects?: boolean;
  /** Include --compressed (default true) so the emitted curl handles gzip. */
  compressed?: boolean;
}

export function emit(spec: RequestSpec, opts: EmitOptions = {}): string {
  const { multiline = true, followRedirects = true, compressed = true } = opts;
  const parts: string[] = ["curl"];

  // Method (-X) — only emit for non-default methods.
  const defaultMethod = spec.bodyType === "none" ? "GET" : "POST";
  if (spec.method !== defaultMethod) {
    parts.push(`-X ${spec.method}`);
  }

  // URL with merged query params for plain curl semantics.
  let url = spec.url;
  const qs = spec.query.filter((kv) => kv.enabled !== false && kv.key);
  if (qs.length) {
    try {
      const u = new URL(url);
      for (const kv of qs) u.searchParams.append(kv.key, kv.value);
      url = u.toString();
    } catch {
      const tail = qs.map((kv) => `${encodeURIComponent(kv.key)}=${encodeURIComponent(kv.value)}`).join("&");
      url += (url.includes("?") ? "&" : "?") + tail;
    }
  }
  parts.push(q(url));

  if (followRedirects && (spec.followRedirects || !spec.unsupported.length)) parts.push("--location");
  if (compressed) parts.push("--compressed");
  if (spec.insecure) parts.push("--insecure");

  // Basic auth: prefer -u so secrets aren't baked into a header literal.
  // (If the header is also present, drop it to avoid double-auth.)
  let headers = spec.headers.filter((h) => h.enabled !== false && h.key);
  if (spec.auth.kind === "basic" && (spec.auth.fields.username || spec.auth.fields.password)) {
    parts.push(`-u ${q(`${spec.auth.fields.username ?? ""}:${spec.auth.fields.password ?? ""}`)}`);
    headers = headers.filter((h) => h.key.toLowerCase() !== "authorization");
  }

  for (const h of headers) {
    parts.push(`-H ${q(`${h.key}: ${h.value}`)}`);
  }

  if (spec.bodyType === "multipart") {
    // body is "key=value\nkey=value"
    for (const line of (spec.body ?? "").split("\n")) {
      const eq = line.indexOf("=");
      if (eq < 0) continue;
      parts.push(`-F ${q(line)}`);
    }
  } else if (spec.bodyType !== "none" && spec.body != null && spec.body !== "") {
    parts.push(`--data-raw ${q(spec.body)}`);
  }

  return multiline ? parts.join(" \\\n  ") : parts.join(" ");
}
